import { create } from "zustand";

import type { SelectedWriter } from "../types/writer";
import type { SelectedTrack } from "./multiSongStore";

// Type for storing writer assignments per song
export type SongWriterAssignment = {
  songId: string;
  songTitle: string;
  assignedWriters: string[]; // Array of writer codes
};

// Type for the complete song-writer matrix
export type SongWriterMatrix = {
  [songId: string]: {
    [writerCode: string]: boolean;
  };
};

// Define the store state
type SongWriterAssignmentStoreState = {
  assignments: SongWriterMatrix;
  songWriterAssignments: SongWriterAssignment[];
};

// Define the store actions
type SongWriterAssignmentStoreActions = {
  toggleWriterForSong: (songId: string, writerCode: string) => void;
  setWriterForSong: (songId: string, writerCode: string, assigned: boolean) => void;
  assignWriterToAllSongs: (writerCode: string, assigned: boolean) => void;
  assignAllWritersToSong: (songId: string, assigned: boolean) => void;
  getSongAssignments: (songId: string) => string[];
  getWriterAssignments: (writerCode: string) => string[];
  initializeMatrix: (songs: SelectedTrack[], writers: SelectedWriter[]) => void;
  getAssignmentsForSong: (songId: string) => SelectedWriter[];
  getAllAssignments: () => SongWriterAssignment[];
  getCreateSongData: () => { [songId: string]: { assignedWriters: string[] } };
  clearState: () => void;
};

// Create the store
const useSongWriterAssignmentStore = create<SongWriterAssignmentStoreState & SongWriterAssignmentStoreActions>((set, get) => ({
  assignments: {},
  songWriterAssignments: [],

  toggleWriterForSong: (songId, writerCode) => {
    const { assignments } = get();
    const currentValue = assignments[songId]?.[writerCode] || false;

    set(state => ({
      assignments: {
        ...state.assignments,
        [songId]: {
          ...state.assignments[songId],
          [writerCode]: !currentValue,
        },
      },
    }));
  },

  setWriterForSong: (songId, writerCode, assigned) => {
    set(state => ({
      assignments: {
        ...state.assignments,
        [songId]: {
          ...state.assignments[songId],
          [writerCode]: assigned,
        },
      },
    }));
  },

  assignWriterToAllSongs: (writerCode, assigned) => {
    const { assignments } = get();
    const updatedAssignments = { ...assignments };

    Object.keys(updatedAssignments).forEach((songId) => {
      updatedAssignments[songId] = {
        ...updatedAssignments[songId],
        [writerCode]: assigned,
      };
    });

    set({ assignments: updatedAssignments });
  },

  assignAllWritersToSong: (songId, assigned) => {
    const { assignments } = get();
    const songAssignments = assignments[songId];
    if (typeof songAssignments === "undefined")
      return;

    const updatedSongAssignments = { ...songAssignments };

    Object.keys(updatedSongAssignments).forEach((writerCode) => {
      updatedSongAssignments[writerCode] = assigned;
    });

    set(state => ({
      assignments: {
        ...state.assignments,
        [songId]: updatedSongAssignments,
      },
    }));
  },

  getSongAssignments: (songId) => {
    const { assignments } = get();
    const songAssignments = assignments[songId];
    if (typeof songAssignments === "undefined")
      return [];
    return Object.keys(songAssignments).filter(writerCode => songAssignments[writerCode]);
  },

  getWriterAssignments: (writerCode) => {
    const { assignments } = get();
    return Object.keys(assignments).filter(songId => assignments[songId]?.[writerCode]);
  },

  initializeMatrix: (songs, writers) => {
    const { assignments: currentAssignments } = get();
    const newAssignments: SongWriterMatrix = { ...currentAssignments };

    songs.forEach((song) => {
      if (song.isSelected) {
        // Only initialize if this song doesn't already exist in assignments
        if (typeof newAssignments[song.id] === "undefined") {
          newAssignments[song.id] = {};
        }

        writers.forEach((writer) => {
          // Only initialize if this writer for this song doesn't already exist
          if (typeof newAssignments[song.id][writer.writercode] === "undefined") {
            newAssignments[song.id][writer.writercode] = false;
          }
        });
      }
    });

    set({ assignments: newAssignments });
  },

  getAssignmentsForSong: (_songId) => {
    // This would return the selected writers for a specific song
    // We'll need access to the writer store to get the full writer objects
    return [];
  },

  getAllAssignments: () => {
    const { assignments } = get();
    const result: SongWriterAssignment[] = [];

    Object.keys(assignments).forEach((songId) => {
      const assignedWriters = Object.keys(assignments[songId]).filter(
        writerCode => assignments[songId][writerCode],
      );

      result.push({
        songId,
        songTitle: songId, // We'll need to get the actual title from album store
        assignedWriters,
      });
    });

    return result;
  },

  // Get assignment data in a format ready for createSong mutation
  getCreateSongData: () => {
    const { assignments } = get();
    const songData: { [songId: string]: { assignedWriters: string[] } } = {};

    Object.keys(assignments).forEach((songId) => {
      const assignedWriters = Object.keys(assignments[songId]).filter(
        writerCode => assignments[songId][writerCode],
      );
      songData[songId] = { assignedWriters };
    });

    return songData;
  },

  clearState: () => {
    set({
      assignments: {},
      songWriterAssignments: [],
    });
  },
}));

export default useSongWriterAssignmentStore;
